// Every installed device of the TICKED device types — the panel under the
// Device Types table (Super Admin). Rows come from the estate-wide tracking
// list (core/admin/deviceTracking, GetDeviceTrackingList), loaded once by the
// page and filtered here, so ticking a type costs no request.
//
// Device · unique number · location · room · owner · users · last seen · code
// flashed · bin file · update — with a Type column when more than one type is
// ticked. A search box narrows by name, number, place or owner.

import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { applyFilters } from '../../../core/admin/deviceTracking';
import { copyText } from '../../../core/utils/clipboard';

const when = (ms) => (ms == null ? 'never'
  : new Date(ms).toLocaleString([], {
    day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit',
  }));

/**
 * types:   the ticked DeviceType rows ({ DeviceID, DeviceName, … })
 * rows:    trackingRowOf rows for the whole estate (null while loading)
 * error:   a message when the list could not be loaded
 * onClear: untick everything
 * onRetry: load the estate list again
 */
export default function DeviceTypeDevicesPanel({
  types, rows, error, onClear, onRetry,
}) {
  const [search, setSearch] = useState('');
  const [copied, setCopied] = useState('');

  const ids = useMemo(() => new Set(types.map((t) => String(t.DeviceID))), [types]);
  const all = useMemo(
    () => (rows ? rows.filter((r) => ids.has(String(r.deviceId))) : []),
    [rows, ids],
  );
  const shown = useMemo(() => applyFilters(all, { search }), [all, search]);
  const live = all.filter((r) => r.isLive).length;
  const many = types.length > 1;

  const heads = [
    ...(many ? ['Type'] : []),
    'Device', 'Unique No.', 'Location', 'Room', 'Owner', 'Users', 'Last seen',
    'Code flashed', 'Bin file', 'Update',
  ];

  return (
    <Card sx={{ mt: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', p: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>Installed devices</Typography>
        {types.map((t) => (
          <Chip key={t.DeviceID} size="small" label={`${t.DeviceID} · ${t.DeviceName || '—'}`} />
        ))}
        {rows && !error && (
          <Typography variant="body2" color="text.secondary">
            {`${all.length} installed · ${live} online`}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {rows && all.length > 0 && (
          <TextField
            size="small" placeholder="Search name / number / place / owner…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ minWidth: 260 }}
          />
        )}
        <Button size="small" onClick={onClear}>Clear selection</Button>
      </Box>

      {error && (
        <Alert
          severity="warning" sx={{ m: 1.5, mt: 0 }}
          action={onRetry ? <Button color="inherit" size="small" onClick={onRetry}>Retry</Button> : null}
        >
          {error}
        </Alert>
      )}
      {!error && !rows && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      )}
      {!error && rows && all.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {many ? 'No device of these types is installed.' : 'No device of this type is installed.'}
        </Typography>
      )}
      {!error && rows && all.length > 0 && (
        <TableContainer sx={{ maxHeight: '55vh' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {heads.map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.uno} hover>
                  {many && <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.deviceTypeName || r.deviceId}</TableCell>}
                  <TableCell sx={{ fontWeight: 600 }}>{r.deviceName}</TableCell>
                  <TableCell>
                    <Tooltip title={copied === r.uno ? 'Copied' : 'Click to copy'}>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', cursor: 'pointer' }}
                        onClick={async () => { if (await copyText(r.uno)) setCopied(r.uno); }}
                      >
                        {r.uno}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{r.locationName}</TableCell>
                  <TableCell>{r.roomName}</TableCell>
                  <TableCell>{r.ownerName || '—'}</TableCell>
                  <TableCell align="center">
                    {r.assigned ? r.mappedUsers : <Chip size="small" variant="outlined" label="unassigned" />}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box component="span" sx={{ color: r.isLive ? 'success.main' : 'text.disabled', mr: 0.5 }}>●</Box>
                    {r.isLive ? 'online' : when(r.lastSeen)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.flashedAt != null ? when(r.flashedAt) : '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {r.binFile || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.needsUpdate
                      ? <Chip size="small" color="warning" label={r.updateMarked ? 'marked' : 'due'} />
                      : (r.firmwareUnknown ? <Typography variant="body2" color="text.secondary">?</Typography> : '✓')}
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={heads.length} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    Nothing matches the search.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
}
